import { apiConfig } from "../config/apiConfig";
import { apiCaching } from "../config/cachingConfig";
import { cachedAPIWrapper } from "../apiCaching/apiWrapper";
import { getImageReplacemnt } from "./imageHelper";
import { handleRatingDisplayChanges } from "./ratingDisplay";
import { checkIncognito } from "../services/worker-injector";

// Constants
const DEFAULT_PAGE_SIZE = {
  DESKTOP: 30,
  MOBILE: 4
};
const DEFAULT_RATING = 5;

// Flag to control which testimonial code to use
const useNewTestimonial = true;  // Set to true for the TestimonialVDOM, false for the old Testimonial

export class Testimonial {
  private pageNumber: number = 1;
  private readonly pageSize: number;
  private readonly conceptId: string;
  private readonly countryId: string;
  private wrapperElement: HTMLElement | null = null;
  private selectedStarRating: number = 0;
  private overAllRatings: string = '5/5';
  private isLoading: boolean = false;
  
  // DOM Elements
  private readonly popoverElement: HTMLElement | null;
  private readonly upArrow: HTMLElement | null;
  private readonly downArrow: HTMLElement | null;
  private readonly resetLink: HTMLElement | null;
  
  // Singleton instance
  private static instance: Testimonial;

  static getInstance(): Testimonial {
    if (!Testimonial.instance) {
      Testimonial.instance = new Testimonial();
    }
    return Testimonial.instance;
  }
  private constructor() {
    // Initialize DOM elements
    this.resetLink = document.getElementById('resetLink');
    this.upArrow = document.getElementById('upArrow');
    this.downArrow = document.getElementById('downArrow');
    this.popoverElement = document.getElementById('popover');
    
    // Initialize properties
    this.pageSize = window.innerWidth >= 768 ? DEFAULT_PAGE_SIZE.DESKTOP : DEFAULT_PAGE_SIZE.MOBILE;
    this.conceptId = (document.getElementById("conceptId") as HTMLInputElement)?.value;
    this.countryId = this.determineCountryId();
    
    // Setup event listeners after DOM is loaded
    document.addEventListener('DOMContentLoaded', () => this.initializeComponent());
  }
  
  private determineCountryId(): string {
    const countryCode = sessionStorage.getItem('countryCode') || 
                       (document.getElementById("countryCode") as HTMLInputElement)?.value || '';
    return countryCode.toLowerCase() === "us" ? "1" : "2";
  }
  
  private initializeComponent(): void {
    const pageType = (document.getElementById('pageType') as HTMLInputElement)?.value;
    if (!pageType || pageType.toLowerCase() === "opus 2 location map") {
      return;
    }
    
    const testimonialDOMisPresent = document.querySelector(".testimonials-section");
    if (!testimonialDOMisPresent) {
      return;
    }
    
    this.initializeUI();
    this.setupEventListeners();
    this.getData();
    this.getTotalReviews();
  }
  
  private initializeUI(): void {
    this.wrapperElement = document.querySelector('.testimonials-section .container');
    if (this.wrapperElement) {
      this.wrapperElement.insertAdjacentHTML('beforeend', `
        <div class="testi-btn-wrapper">                
          <a href="#" class="primary-btn">See More...</a>                
        </div>
      `);
    }
  }
  
  private setupEventListeners(): void {
    const seeMoreButton = document.querySelector('.testi-btn-wrapper .primary-btn');
    const starRatingDropdown = document.getElementById('popoverBtn');
    const starsDisplay = document.getElementById('starsDisplay');
    
    seeMoreButton?.addEventListener('click', (e) => this.seeMoreData(e));
    starRatingDropdown?.addEventListener('click', () => this.handleDropdownToggle());
    starsDisplay?.addEventListener('click', () => this.handleDropdownToggle());
    this.resetLink?.addEventListener('click', () => this.onResetClick());
  }

  private async getData(): Promise<void> {
    if (this.isLoading) return;
    
    this.isLoading = true;
    
    try {
      const weblocationId = this.getWebLocationId();
      const ratingParam = this.selectedStarRating || DEFAULT_RATING;
      const url = this.buildApiUrl(weblocationId, ratingParam);
      
      const result = await cachedAPIWrapper(url, apiCaching.TestimonyAndReview);
      
      if (!result?.length) {
        this.showNoReviewSection();
        return;
      }
      
      this.hideNoReviewSection();
      this.updateUIAfterDataLoad(result);
      this.renderTestimonials(result);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      this.handleDataError();
    } finally {
      this.isLoading = false;
    }
  }
  
  private getWebLocationId(): string {
    return localStorage.getItem("franchiseWebLocationId") || 
           (document.getElementById('weblocationId') as HTMLInputElement)?.value || '';
  }
  
  private buildApiUrl(weblocationId: string, rating: number): string {
    // Params order: {ConceptId}/{FranchiseWebLocationId}/{CountryId}/{MinimumRating}/{PageNumber}/{PageSize}/{StarRating}
    return `${apiConfig.TESTIMONIAL_REVIEWS}/${this.conceptId}/${weblocationId || 0}/` +
           `${this.countryId}/0/${this.pageNumber}/${this.pageSize}/${rating}`;
  }
  
  private updateUIAfterDataLoad(result: TestimonialData[]): void {
    const seeMoreButton = document.querySelector('.testi-btn-wrapper') as HTMLElement;
    this.wrapperElement?.classList.remove('hidden');
    
    if (seeMoreButton) {
      const shouldShowLoadMore = result.length > 0 && result.length >= this.pageSize;
      seeMoreButton.style.display = shouldShowLoadMore ? 'flex' : 'none';
    }
  }
  
  private renderTestimonials(testimonials: TestimonialData[]): void {
    const testimonialsList = document.querySelector('.testimonials-section .container .testimonial-wrapper');
    if (!testimonialsList) return;
    
    const html = testimonials.map(item => this.createTestimonialHTML(item)).join('');
    testimonialsList.innerHTML = this.pageNumber === 1 ? html : testimonialsList.innerHTML + html;
  }
  
  private handleDataError(): void {
    this.wrapperElement?.classList.add('hidden');
    this.showNoReviewSection();
  }
  
  public generateRatingStars(rating: number): string {
    const fullStar = '<span class="star full">★</span>';
    const emptyStar = '<span class="star empty">☆</span>';
    const numberOfFullStars = Math.round(rating);
    const hasHalfStar = rating % 1 !== 0;
    const numberOfEmptyStars = hasHalfStar ? 5 - numberOfFullStars - 1 : 5 - numberOfFullStars;
    return fullStar.repeat(numberOfFullStars) + (hasHalfStar ? '<span class="star half">★</span>' : '') + emptyStar.repeat(numberOfEmptyStars);
  }
  public createTestimonialHTML(data: TestimonialData): string {
    const fullName: string = data.reviewAuthorName
    const processedNameArray = fullName.match(/\b[a-zA-Z]+\b/g) as string[]
    let displayName = fullName;
        if(processedNameArray && processedNameArray.length === 1){
            displayName = processedNameArray[0];
        }
        if(processedNameArray && processedNameArray.length >= 2){
            displayName = `${processedNameArray[0]} ${processedNameArray[1].charAt(0).toUpperCase()}.`
        }
    let photoDiv: string | ReturnType<typeof getImageReplacemnt> = '';
    if(data.reviewPhotoUrl === null){
      photoDiv = getImageReplacemnt(displayName,'testimonial');
    }   
    else{
      photoDiv = `<div class="testi-profile"><img src="${data.reviewPhotoUrl}" alt="Profile image for ${displayName}" width="72" height="73"></div>`;  
    }
    return `
        <li class="testi-main-wrap">
          <div class="testi-main">
            <div class="testi-details">
              ${photoDiv as string}
              <div class="testi-bio">
                <strong class="user-name">${displayName}</strong>
                ${data.city ? `<address class="address">${data.city}</address>` : ''}
                <div class="rating" role="img" aria-label=${`Rating: ${data.reviewStarRating} out of 5 stars`}>
                  ${this.generateRatingStars(data.reviewStarRating ? Math.round(data.reviewStarRating) : 0)}
                </div>
                ${data.reviewSourceType ?? "" ? `<span class="review-source">via ${data.reviewSourceType}</span>` : ""}
              </div>
            </div>
            <div class="testi-comment">
            ${data.reviewBody ? `<p class="body-text-sm">“${data.reviewBody}”</p>` : ''}
             ${data.reviewResponseBody ? `<div class="testi-reply">
                <p class="body-text-sm">Owner Response:</p>
                <p class="body-text-sm">“${data.reviewResponseBody}”</p>
              </div>` : ''}
            </div>
          </div>
        </li>
      `;
  }
  private async seeMoreData(event: Event): Promise<void> {
    event.preventDefault();
    this.pageNumber++;
    
    try {
      const weblocationId = localStorage.getItem("franchiseWebLocationId") || '0';
      const ratingParam = this.selectedStarRating || DEFAULT_RATING;
      const url = this.buildApiUrl(weblocationId, ratingParam);
      
      const result = await cachedAPIWrapper(url, apiCaching.TestimonyAndReview) as TestimonialData[];
      
      if (!result?.length) {
        this.showNoReviewSection();
        return;
      }
      
      this.hideNoReviewSection();
      this.appendTestimonials(result);
      this.updateLoadMoreButton(result.length);
    } catch (error) {
      console.error('Error loading more testimonials:', error);
      this.showNoReviewSection();
    }
  }
  
  private appendTestimonials(testimonials: TestimonialData[]): void {
    const testimonialsList = document.querySelector('.testimonials-section .container .testimonial-wrapper');
    if (!testimonialsList) return;
    
    const html = testimonials.map(item => this.createTestimonialHTML(item)).join('');
    testimonialsList.insertAdjacentHTML('beforeend', html);
  }
  
  private updateLoadMoreButton(resultLength: number): void {
    const seeMoreButton = document.querySelector('.testi-btn-wrapper') as HTMLElement;
    if (!seeMoreButton) return;
    
    const shouldShowLoadMore = resultLength > 0 && resultLength >= this.pageSize;
    seeMoreButton.style.display = shouldShowLoadMore ? 'flex' : 'none';
  }

  private handleStarRatingFilter() {
    const ratingForm = document.getElementById("ratingForm");
    if (ratingForm) {
        const radioButtons = ratingForm.querySelectorAll('input[name="rating"]');
        // Add a change event listener to each radio button
        radioButtons?.forEach((radioButton) => {
            (radioButton as HTMLInputElement).onclick =  () => {
                const selectedRadio = ratingForm.querySelector('input[name="rating"]:checked') as HTMLInputElement;
                if (selectedRadio) {
                    this.selectedStarRating = Number.parseInt(selectedRadio.value, 10);
                    this.pageNumber = 1;//reset page number to 1 on selecting star filters.
                    this.getData();
                    this.showHideRatingDrpDwn();
                    this.updateStarsDisplay(this.selectedStarRating);
                    this.handleRatingDisplayChanges();
                }
            }
        });
      } 
  }

  private handleDropdownToggle() {
    if (this.popoverElement) {
      this.showHideRatingDrpDwn()
      this.handleStarRatingFilter();
    }
  }

  private showHideRatingDrpDwn() {
    this.popoverElement?.classList?.toggle("hidden");
    this.upArrow?.classList?.toggle("hidden");
    this.downArrow?.classList?.toggle("hidden");
  }

  private updateStarsDisplay(rating: number) {
    const starsContainer = document.getElementById('starsDisplay');
    if (starsContainer) {
      starsContainer.innerHTML = "";
      for (let i = 0; i < rating; i++) {
        const starElement = document.createElement("span");
        starElement.className = "star full";
        starElement.innerHTML = '&#9733;'; 
        starsContainer.appendChild(starElement);
      }
    }
  }

  private handleRatingDisplayChanges() {
    handleRatingDisplayChanges(this.resetLink);
  }

  private onResetClick(){
    const ratingText = document.getElementById('ratingText');
    const ratingsInfo = document.getElementById('ratingsInfo');
    if(ratingText){
      ratingText.textContent = this.overAllRatings;
      ratingText.classList.remove('showing-text');
      ratingText.classList.add('ratings-text');
    }
    this.resetLink?.classList.add('hidden');
    this.updateStarsDisplay(5);
    const ratingForm = document.getElementById("ratingForm");
    if (ratingForm) {
        const radioButtons = ratingForm.querySelectorAll<HTMLInputElement>('input[name="rating"]');
        // Add a change event listener to each radio button
        radioButtons.forEach(radio => radio.checked = false);
    }
    ratingsInfo?.classList.remove('hidden');
    ratingsInfo?.classList.add('ratings-info');
    this.selectedStarRating = 0;
    this.pageNumber = 1;
    this.getData();
  }

  async getTotalReviews(): Promise<void> {
    try {
      const weblocationId = this.getWebLocationId();
      const url = `${apiConfig.TESTIMONIAL_REVIEWS}/${this.conceptId}/${weblocationId || 0}/${this.countryId}/1/1/1`;
      
      const result = await cachedAPIWrapper(url, apiCaching.TestimonyAndReview) as TestimonialData[];
      
      if (!result?.[0]) return;
      
      this.updateReviewCount(result[0]);
      this.updateOverallRating(result[0]);
    } catch (error) {
      console.error('Unable to fetch total ratings:', error);
    }
  }
  
  private updateReviewCount(data: any): void {
    const totalReviews = document.querySelector("#ratingsInfo p");
    if (totalReviews && data.grandTotal !== undefined) {
      totalReviews.textContent = `${data.grandTotal} Ratings`;
    }
  }
  
  private updateOverallRating(data: any): void {
    const overallRatingDiv = document.getElementById("ratingText");
    if (overallRatingDiv && data.overallRating !== undefined) {
      this.overAllRatings = `${data.overallRating}/5`;
      overallRatingDiv.textContent = this.overAllRatings;
    }
  }

  private toggleReviewSections(showNoReview: boolean): void {
    const testimonialsSection = document.querySelector('.testimonials-section');
    const noReviewSection = document.querySelector('.no-review-section');
    
    if (showNoReview) {
      testimonialsSection?.classList.add('hidden');
      noReviewSection?.classList.remove('hidden');
    } else {
      testimonialsSection?.classList.remove('hidden');
      noReviewSection?.classList.add('hidden');
    }
  }
  
  private showNoReviewSection(): void {
    this.toggleReviewSections(true);
  }
  
  private hideNoReviewSection(): void {
    this.toggleReviewSections(false);
  }
}

// Module-scoped initialization state to prevent race conditions across multiple calls
type InitState = 'idle' | 'in_progress' | 'done';
let testimonialInitState: InitState = 'idle';

document.addEventListener('DOMContentLoaded', async () => {
  // Prevent duplicate initialization across multiple calls/retries
  
    async function handleRocketData(){
      if (testimonialInitState !== 'idle') return;
      testimonialInitState = 'in_progress';
      try {
        const isIncognito = await checkIncognito();
        if(isIncognito){
          const { TestimonialVDOM } = await import(/* webpackChunkName: "testimonial-vdom" */ './testimonial-card-VDOM');
          await TestimonialVDOM.create();
          testimonialInitState = 'done';
        }
        const rocketData = sessionStorage.getItem('rocketEnabled') === 'true';
        const rocketDisabledData = sessionStorage.getItem('rocketDisabled') === 'true';
        const neighborlyBrand = (document.getElementById("conceptId") as HTMLInputElement)?.value;
        //If its neighborly.com always call testimonial-vdom
        if (rocketData && neighborlyBrand !== '19') {
          const { TestimonialRocket } = await import(/* webpackChunkName: "testimonial-rocket" */ './testimonial-poc');
          const instance  = TestimonialRocket.getTestimonialInstance();
          instance.domLookup();
          instance.getData();
          testimonialInitState = 'done';
          return;
        } else if (rocketDisabledData) {
          const { TestimonialVDOM } = await import(/* webpackChunkName: "testimonial-vdom" */ './testimonial-card-VDOM');
          await TestimonialVDOM.create();
          testimonialInitState = 'done';
          return;
        } else {
          // Retry up to 5 times, then deterministically load VDOM as fallback
          let count = 0;
          const retryInterval = setInterval(async () => {
            if (testimonialInitState === 'done') {
              clearInterval(retryInterval);
              return;
            }

            const enabled = sessionStorage.getItem('rocketEnabled') === 'true';
            const disabled = sessionStorage.getItem('rocketDisabled') === 'true';
            //If its neighborly.com always call testimonial-vdom
            if (enabled && neighborlyBrand !== '19') {
              clearInterval(retryInterval);
              const { TestimonialRocket } = await import(/* webpackChunkName: "testimonial-rocket" */ './testimonial-poc');
              const instance  = TestimonialRocket.getTestimonialInstance();
              instance.domLookup();
              instance.getData();
              testimonialInitState = 'done';
              return;
            }

            if (disabled || count === 5) {
              clearInterval(retryInterval);
              const { TestimonialVDOM } = await import(/* webpackChunkName: "testimonial-vdom" */ './testimonial-card-VDOM');
              await TestimonialVDOM.create();
              testimonialInitState = 'done';
              return;
            }

            count++;
          }, 500);
        }
      } catch (e) {
        // Reset to idle on failure to allow retry logic to kick in
        testimonialInitState = 'idle';
        throw e;
      }
  }

  const pageType = (document.getElementById('pageType') as HTMLInputElement)?.value; 

  if (pageType && pageType.toLocaleLowerCase() !== "opus 2 location map") {
    const testimonialDOMisPresent = document.querySelector(".testimonials-section");
    
    if(testimonialDOMisPresent) {
      handleRocketData();
    }
  }
});

type TestimonialData = {
  reviewPhotoUrl: string | null;
  reviewAuthorName: string;
  city?: string;
  reviewStarRating: number;
  reviewSourceType?: string;
  reviewBody?: string;
  reviewResponseBody?: string;
}
